




import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { 
  Layout, Users, TrendingUp, CheckCircle, 
  Mail, Lock, Eye, EyeOff, Loader2 
} from "lucide-react";
import Swal from "sweetalert2";

import APIConfig from "../Common/Configurations/APIConfig";
import Auth from "../Common/Configurations/Auth";
import PublicHeader from "../Navigation/PublicHeader";

function PublicScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // Toggle state

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);

    try {
      const response = await axios.post(
        APIConfig.APIACTIVEURL + APIConfig.LOGIN,
        { email, password }
      );

      Auth.login(response.data);
      
      Swal.fire({
        icon: 'success',
        title: 'Welcome Back!',
        // text: 'Access granted. Opening dashboard...',
        text: `Logged in as ${response.data.fullName}`,
        timer: 1500,
        showConfirmButton: false
      });

      navigate("/dashboard");
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: 'Invalid credentials. Please verify your email and password.',
      });
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: "calc(100vh - 70px)",
      display: "flex",
      backgroundColor: "#f8fafc",
      fontFamily: "'Inter', sans-serif"
    },
    leftSection: {
      flex: 1.2, // Slightly wider for marketing
      padding: "80px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      backgroundColor: "#0f172a", // Darker premium navy
      color: "#fff",
      backgroundImage: "radial-gradient(circle at 20% 20%, rgba(79, 70, 229, 0.15) 0%, transparent 50%)",
    },
    rightSection: {
      flex: 0.8,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "40px",
      backgroundColor: "#f8fafc"
    },
    title: { fontSize: "52px", fontWeight: "900", marginBottom: "25px", lineHeight: "1.1", letterSpacing: "-1.5px" },
    subtitle: { fontSize: "19px", color: "#94a3b8", marginBottom: "48px", maxWidth: "520px", lineHeight: "1.6" },
    featureItem: { display: "flex", alignItems: "center", gap: "15px", marginBottom: "24px", fontSize: "16px", color: "#cbd5e1", fontWeight: "500" },
    loginCard: {
      width: "100%",
      maxWidth: "400px",
      backgroundColor: "#fff",
      padding: "45px",
      borderRadius: "28px",
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.08)",
      border: "1px solid #eef2f6"
    },
    label: { fontSize: "13px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" },
    inputContainer: { position: "relative", marginBottom: "20px" },
    input: {
      width: "100%",
      padding: "14px 15px 14px 45px",
      borderRadius: "14px",
      border: "1.5px solid #e2e8f0",
      outline: "none",
      fontSize: "15px",
      boxSizing: "border-box",
      transition: "0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      backgroundColor: "#fcfdfe"
    },
    loginBtn: {
      width: "100%",
      padding: "16px",
      backgroundColor: "#4f46e5",
      color: "#fff",
      border: "none",
      borderRadius: "14px",
      fontSize: "16px",
      fontWeight: "700",
      cursor: loading ? "not-allowed" : "pointer",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "12px",
      boxShadow: "0 10px 15px -3px rgba(79, 70, 229, 0.3)",
      transition: "0.3s",
      opacity: loading ? 0.7 : 1
    },
    eyeIcon: {
      position: "absolute",
      right: "15px",
      top: "13px",
      color: "#94a3b8",
      cursor: "pointer",
      zIndex: 5
    }
  };

  return (
    <>
      <PublicHeader />
      <div style={styles.container}>
        {/* Left Side: Professional Branding */}
        <div style={styles.leftSection}>
          <h1 style={styles.title}>Precision meets Real Estate Management.</h1>
          <p style={styles.subtitle}>
            A complete enterprise suite to digitize your plot inventory, 
            empower your staff, and accelerate your sales pipeline.
          </p>

          <div>
            <div style={styles.featureItem}><CheckCircle size={20} color="#10b981" /> Interactive Project Portfolio</div>
            <div style={styles.featureItem}><Users size={20} color="#10b981" /> Comprehensive CRM & Lead Engine</div>
            <div style={styles.featureItem}><TrendingUp size={20} color="#10b981" /> Financial Transaction Intelligence</div>
            <div style={styles.featureItem}><Layout size={20} color="#10b981" /> Micro-Precision Unit Management</div>
          </div>
        </div>

        {/* Right Side: High-End Login Card */}
        <div style={styles.rightSection}>
          <div style={styles.loginCard}>
            <div style={{ textAlign: "center", marginBottom: "35px" }}>
              <h2 style={{ margin: 0, fontSize: "28px", color: "#0f172a", fontWeight: "800" }}>Welcome Back</h2>
              <p style={{ color: "#64748b", marginTop: "8px", fontSize: "14px" }}>Sign in with Email or Phone.</p>
            </div>

            <form onSubmit={handleLogin}>
              <div style={styles.inputContainer}>
                <label style={styles.label}>Login (Email/Phone)</label>
                <div style={{ position: 'relative' }}>
                   <Mail size={18} style={{ position: "absolute", left: "15px", top: "14px", color: "#94a3b8" }} />
                   <input
                    type="text"
                    placeholder="Email or Phone Number"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div style={styles.inputContainer}>
                <label style={styles.label}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={18} style={{ position: "absolute", left: "15px", top: "14px", color: "#94a3b8" }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder=""
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ ...styles.input, paddingRight: '50px' }}
                    required
                  />
                  <div 
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.eyeIcon}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                style={styles.loginBtn}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    Signing...
                  </>
                ) : (
                  "Sign in "
                )}
              </button>
            </form>

            <div style={{ textAlign: "center", marginTop: "30px", fontSize: "12px", color: "#94a3b8", fontWeight: "600", letterSpacing: "1px", textTransform: "uppercase" }}>
              
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
}

export default PublicScreen;